import {
  FETCH_ESTIMATE,
  FETCH_ESTIMATE_SUCCESS,
  FETCH_ESTIMATE_FAILED,
  CLEAR_ESTIMATE
} from "../store/types";
import Polyline from '@mapbox/polyline';
import { firebase } from '../config/configureFirebase';
import { FareCalculator } from '../other/FareCalculator';
import { GetDistance } from '../other/GeoFunctions';
import { onValue, get } from "firebase/database";
import { mergeCarTypeWithCity } from './zonificacionactions';
import { applySmartFareToTotals } from '../other/smartFare';
import store from '../store/store';
import { startMetric, endMetric, logWarn } from '../utils/performanceMonitor';

async function resolveCarDetailsForEstimate(carDetails, bookingData, settings) {
  if (!carDetails) return carDetails;
  if (settings && settings.useCityFares === false) return carDetails;
  // Prioridad: ciudad del viaje (booking/estimate) — no el perfil del pasajero.
  const cityId = bookingData.cityId || null;
  if (!cityId) return carDetails;

  let city = null;
  const cities = store.getState()?.zonificacion?.cities;
  if (cities && cities.length) {
    city = cities.find((c) => c.id === cityId);
  }
  if (!city) {
    try {
      const { zonificacionCityEditRef } = firebase;
      const snap = await get(zonificacionCityEditRef(cityId));
      city = snap.val();
    } catch (e) {
      return carDetails;
    }
  }
  if (!city || !city.carTypes) return carDetails;
  return mergeCarTypeWithCity(carDetails, city.carTypes);
}

function dispatchFareSuccess(dispatch, bookingData, carDetails, settings, distance, timeSecs, waypoints, metric) {
  const baseFare = FareCalculator(distance, timeSecs, carDetails, bookingData.instructionData, settings.decimal);
  if (baseFare.error) {
    logWarn('estimate: FareCalculator soft-fail', { error: baseFare.error });
    endMetric(metric, false, { error: baseFare.error });
    dispatch({
      type: FETCH_ESTIMATE_FAILED,
      payload: "Error calculating fare. Please try again.",
    });
    return;
  }
  const fare = applySmartFareToTotals(baseFare, carDetails, settings);
  const { totalCost, grandTotal, convenience_fees } = fare;
  const smartFareMultiplier = fare.smartFareMultiplier || 1;
  const estimateFareBeforeSmart = baseFare.grandTotal
    ? parseFloat(parseFloat(baseFare.grandTotal).toFixed(settings.decimal))
    : 0;
  endMetric(metric, true, {
    distance,
    timeSecs,
    hasRoute: !!bookingData.routeDetails,
    smartFareMultiplier,
  });
  dispatch({
    type: FETCH_ESTIMATE_SUCCESS,
    payload: {
      pickup: bookingData.pickup,
      drop: bookingData.drop,
      carDetails: carDetails,
      instructionData: bookingData.instructionData,
      estimateDistance: parseFloat(parseFloat(distance).toFixed(settings.decimal)),
      fareCost: totalCost ? parseFloat(parseFloat(totalCost).toFixed(settings.decimal)) : 0,
      estimateFare: grandTotal ? parseFloat(parseFloat(grandTotal).toFixed(settings.decimal)) : 0,
      estimateTime: timeSecs,
      convenience_fees: convenience_fees ? parseFloat(parseFloat(convenience_fees).toFixed(settings.decimal)) : 0,
      smartFareMultiplier,
      estimateFareBeforeSmart,
      waypoints: waypoints
    },
  });
}

export const getEstimate = (bookingData) => async (dispatch) => {
  const   {
      settingsRef
  } = firebase;

  const metric = startMetric('estimate');

  dispatch({
    type: FETCH_ESTIMATE,
    payload: bookingData,
  });

  try {
    const settingsSnap = await get(settingsRef);
    const settingsEarly = settingsSnap.val() || {};
    const carDetails = await resolveCarDetailsForEstimate(
      bookingData.carDetails,
      bookingData,
      settingsEarly
    );

    let res = bookingData.routeDetails;

    if(res){
      let points = Polyline.decode(res.polylinePoints);

      let waypoints = points.map((point) => {
          return {
              latitude: point[0],
              longitude: point[1]
          }
      });

      onValue(settingsRef, settingdata => {
        try {
          let settings = settingdata.val();
          let distance = settings.convert_to_mile? (res.distance_in_km / 1.609344) : res.distance_in_km;
          dispatchFareSuccess(dispatch, bookingData, carDetails, settings, distance, res.time_in_secs, waypoints, metric);
        } catch (error) {
          console.error('[getEstimate] Error con routeDetails:', error);
          endMetric(metric, false, { error: error.message });
          dispatch({
            type: FETCH_ESTIMATE_FAILED,
            payload: "Error calculating estimate: " + error.message,
          });
        }
      }, {onlyOnce:true});
    }else{
      // Si no hay routeDetails (API falló), calcular distancia y tiempo estimados
      // Incluye paradas intermedias (drop.waypoints) para no subestimar multi-parada.
      if (bookingData.pickup && bookingData.pickup.coords && bookingData.drop && bookingData.drop.coords) {
        try {
          const stops = Array.isArray(bookingData.drop.waypoints)
            ? bookingData.drop.waypoints.filter(
                (p) => p && p.lat != null && p.lng != null && !isNaN(Number(p.lat)) && !isNaN(Number(p.lng))
              )
            : [];
          const pathPoints = [
            { lat: bookingData.pickup.coords.lat, lng: bookingData.pickup.coords.lng },
            ...stops.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) })),
            { lat: bookingData.drop.coords.lat, lng: bookingData.drop.coords.lng },
          ];
          let straightLineDistance = 0;
          for (let i = 0; i < pathPoints.length - 1; i++) {
            straightLineDistance += GetDistance(
              pathPoints[i].lat,
              pathPoints[i].lng,
              pathPoints[i + 1].lat,
              pathPoints[i + 1].lng
            );
          }

          const distanceCorrectionFactor = 1.4;
          const estimatedRealDistance = straightLineDistance * distanceCorrectionFactor;

          const averageSpeedKmh = 30;
          const estimatedTimeSeconds = Math.round((estimatedRealDistance / averageSpeedKmh) * 3600);

          // Premium (Uber/inDrive): tarifa por distancia aproximada, sin polilínea recta en el mapa.
          const waypoints = null;

          onValue(settingsRef, settingdata => {
            try {
              let settings = settingdata.val();
              let distance = settings.convert_to_mile? (estimatedRealDistance / 1.609344) : estimatedRealDistance;
              dispatchFareSuccess(dispatch, bookingData, carDetails, settings, distance, estimatedTimeSeconds, waypoints, metric);
            } catch (error) {
              console.error('[getEstimate] Error en settings callback:', error);
              endMetric(metric, false, { error: error.message });
              dispatch({
                type: FETCH_ESTIMATE_FAILED,
                payload: "Error calculating estimate: " + error.message,
              });
            }
          }, {onlyOnce:true});
        } catch (error) {
          console.error('[getEstimate] Error calculando estimación sin routeDetails:', error);
          endMetric(metric, false, { error: error.message });
          dispatch({
            type: FETCH_ESTIMATE_FAILED,
            payload: "Error calculating estimate: " + error.message,
          });
        }
      } else {
        endMetric(metric, false, { error: 'missing_coords' });
        dispatch({
          type: FETCH_ESTIMATE_FAILED,
          payload: "No Route Found - Missing pickup or drop coordinates",
        });
      }
    }
  } catch (error) {
    console.error('[getEstimate] Unexpected error:', error);
    endMetric(metric, false, { error: error.message });
    dispatch({
      type: FETCH_ESTIMATE_FAILED,
      payload: "Error calculating estimate: " + (error.message || 'unknown'),
    });
  }
}

export const clearEstimate = () => (dispatch) => {
    dispatch({
        type: CLEAR_ESTIMATE,
        payload: null,
    });    
}
